import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import {
  AUDIT_ACTION_LABELS,
  fetchAuditLog,
  fetchAuditLogFilterOptions,
  type AuditLogRow,
} from "../api/auditLogApi";
import { downloadCsv } from "../utils/csvDownload";

const PAGE_SIZE = 50;

function formatWhen(iso: string | null) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function actionLabel(action: string) {
  return AUDIT_ACTION_LABELS[action] ?? action;
}

export function AuditLogPage() {
  const { user, can } = useAuth();
  const { showToast } = useToast();
  const mayView = Boolean(user && (user.role === "superadmin" || can("audit_log")));

  const [rows, setRows] = useState<AuditLogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [actionFilter, setActionFilter] = useState("");
  const [moduleFilter, setModuleFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [filterOptions, setFilterOptions] = useState<{
    actions: string[];
    modules: string[];
    users: { id: string; email: string; displayName: string }[];
  }>({ actions: [], modules: [], users: [] });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const out = await fetchAuditLog({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        action: actionFilter || undefined,
        module: moduleFilter || undefined,
        userId: userFilter || undefined,
        search: search || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      });
      setRows(out.rows);
      setTotal(out.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load audit log.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter, moduleFilter, userFilter, search, dateFrom, dateTo]);

  useEffect(() => {
    if (!mayView) return;
    void load();
  }, [load, mayView]);

  useEffect(() => {
    if (!mayView) return;
    void fetchAuditLogFilterOptions()
      .then(setFilterOptions)
      .catch(() => {});
  }, [mayView]);

  useEffect(() => {
    setPage(0);
  }, [actionFilter, moduleFilter, userFilter, search, dateFrom, dateTo]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const exportCsv = () => {
    if (rows.length === 0) {
      showToast("No rows to export on this page.", "info");
      return;
    }
    downloadCsv(
      "audit-log.csv",
      [
        "Time",
        "User",
        "Email",
        "Role",
        "Action",
        "Module",
        "Summary",
        "Method",
        "Path",
        "Status",
        "IP",
      ],
      rows.map((r) => [
        formatWhen(r.occurredAt),
        r.userDisplayName ?? "",
        r.userEmail ?? "",
        r.userRole ?? "",
        actionLabel(r.action),
        r.module ?? "",
        r.summary,
        r.httpMethod ?? "",
        r.httpPath ?? "",
        r.statusCode != null ? String(r.statusCode) : "",
        r.ipAddress ?? "",
      ]),
    );
  };

  const clearFilters = () => {
    setActionFilter("");
    setModuleFilter("");
    setUserFilter("");
    setSearch("");
    setDateFrom("");
    setDateTo("");
  };

  const userOptions = useMemo(
    () =>
      filterOptions.users.map((u) => ({
        id: u.id,
        label: u.displayName ? `${u.displayName} (${u.email})` : u.email,
      })),
    [filterOptions.users],
  );

  if (user && !mayView) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="page-stack">
      <PageHeader
        title="Audit log"
        subtitle="Complete activity trail for the HO console — sign-in, sign-out, views, creates, updates, deletes, and exports."
      />

      <div className="card">
        <div className="card__body">
          <p className="alert alert--info" role="note" style={{ marginTop: 0 }}>
            Activity is recorded in three layers: <strong>API</strong> (every server request),{" "}
            <strong>Page opened</strong> (navigation), and <strong>UI action</strong> (button clicks, form submits,
            dropdown/filter changes, file picks). Passwords and tokens are never stored.
          </p>

          <details className="audit-coverage-details" style={{ marginBottom: "1rem" }}>
            <summary style={{ cursor: "pointer", fontWeight: 600, color: "#374151" }}>
              What is covered in the audit log
            </summary>
            <ul style={{ margin: "0.75rem 0 0", paddingLeft: "1.25rem", color: "#4b5563", lineHeight: 1.6 }}>
              <li>
                <strong>Authentication</strong> — sign in, sign out, failed login, password change
              </li>
              <li>
                <strong>Navigation</strong> — every console page you open
              </li>
              <li>
                <strong>UI actions</strong> — buttons, form submit, filters/dropdowns, file selection, SearchableSelect
                picks
              </li>
              <li>
                <strong>API — View</strong> — all GET calls (lists, dossiers, registers, exports metadata)
              </li>
              <li>
                <strong>API — Create / Update / Delete</strong> — POST, PUT, PATCH, DELETE
              </li>
              <li>
                <strong>API — Import / Export</strong> — bulk uploads, PDF/CSV generation
              </li>
              <li>
                <strong>Denied access</strong> — blocked requests (401 / 403) when not signed in or forbidden
              </li>
              <li>
                <strong>Modules</strong> — Leads, Applicants, Masters, Procurement, Inventory, Projects, Operations,
                Console users, Reports, Geo, Timeline, and more
              </li>
            </ul>
            <p style={{ margin: "0.75rem 0 0", color: "#6b7280", fontSize: "0.875rem" }}>
              Not logged (by design): server health check, auth config poll, and session refresh (<code>/api/auth/me</code>
              ). Login screen actions are recorded on the server when sign-in is attempted.
            </p>
          </details>

          <div className="form-grid form-grid--2" style={{ marginBottom: "1rem" }}>
            <label className="form-field">
              <span className="form-field__label">Search</span>
              <input
                className="form-field__input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Summary, email, or API path"
              />
            </label>
            <label className="form-field">
              <span className="form-field__label">User</span>
              <select className="form-field__input" value={userFilter} onChange={(e) => setUserFilter(e.target.value)}>
                <option value="">All users</option>
                {userOptions.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="form-field">
              <span className="form-field__label">Action</span>
              <select className="form-field__input" value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}>
                <option value="">All actions</option>
                {filterOptions.actions.map((a) => (
                  <option key={a} value={a}>
                    {actionLabel(a)}
                  </option>
                ))}
              </select>
            </label>
            <label className="form-field">
              <span className="form-field__label">Module</span>
              <select className="form-field__input" value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)}>
                <option value="">All modules</option>
                {filterOptions.modules.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </label>
            <label className="form-field">
              <span className="form-field__label">From date</span>
              <input
                type="date"
                className="form-field__input"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </label>
            <label className="form-field">
              <span className="form-field__label">To date</span>
              <input
                type="date"
                className="form-field__input"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </label>
          </div>

          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "1rem" }}>
            <button type="button" className="btn btn--secondary" onClick={() => void load()} disabled={loading}>
              Refresh
            </button>
            <button type="button" className="btn btn--secondary" onClick={clearFilters}>
              Clear filters
            </button>
            <button type="button" className="btn btn--secondary" onClick={exportCsv} disabled={rows.length === 0}>
              Export page CSV
            </button>
          </div>

          {error ? <div className="alert alert--danger">{error}</div> : null}

          {loading ? (
            <p className="text-muted">Loading audit entries…</p>
          ) : (
            <>
              <p className="text-muted" style={{ marginBottom: "0.75rem" }}>
                {total.toLocaleString()} total entries · page {page + 1} of {totalPages}
              </p>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>User</th>
                      <th>Action</th>
                      <th>Module</th>
                      <th>Summary</th>
                      <th>Method</th>
                      <th>Path</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-muted">
                          No audit entries match your filters yet.
                        </td>
                      </tr>
                    ) : (
                      rows.map((r) => (
                        <tr key={r.id}>
                          <td>{formatWhen(r.occurredAt)}</td>
                          <td>
                            <div>{r.userDisplayName || "—"}</div>
                            <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                              {r.userEmail || "—"}
                            </div>
                          </td>
                          <td>{actionLabel(r.action)}</td>
                          <td>{r.module || "—"}</td>
                          <td title={r.summary}>{r.summary}</td>
                          <td>{r.httpMethod || "—"}</td>
                          <td
                            title={r.httpPath ?? ""}
                            style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}
                          >
                            {r.httpPath || "—"}
                          </td>
                          <td>{r.statusCode ?? "—"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div style={{ display: "flex", gap: "0.75rem", marginTop: "1rem", alignItems: "center" }}>
                <button
                  type="button"
                  className="btn btn--secondary"
                  disabled={page <= 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="btn btn--secondary"
                  disabled={page + 1 >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
